import { AssetPermission, PermissionName } from '../../../types/asset-permissions.types'
import { EntityTypes } from '../../../../../redux/slices/modal'
import { fireHeapEvent } from '@dtx-company/inter-app/src/event-tracking/helpers'
import { useAssetPermissionsState } from '../../../hooks/asset-permissions/useAssetPermissionsState'
import { useAuthState } from '../../../../../hooks/useAuthState'
import { useCallback, useMemo } from 'react'
import eventNames from '@dtx-company/inter-app/src/event-tracking/constants/domainsV2'

export interface ShareWithEntireOrgState {
  organizationPermission: AssetPermission
  label: string
  checkboxChecked: boolean
  onChangeCheckbox: () => void
}

export interface ShareWithEntireOrgProps {
  assetId: string
  isWhitelabel?: boolean
  isCodeSubdomain?: boolean
  trackingClickShareWithEveryone?: (assetId: string, checked: boolean) => void
  /** Whether to use the past sense on "Share" in the label */
  pastTenseLabel?: boolean
}

export function useShareWithEntireOrgState({
  assetId,
  isWhitelabel,
  isCodeSubdomain,
  trackingClickShareWithEveryone,
  pastTenseLabel
}: ShareWithEntireOrgProps): ShareWithEntireOrgState {
  const { jwt } = useAuthState()
  const orgId = jwt?.org?.orgId ?? ''
  const orgName = jwt?.org?.name ?? ''

  const assetPermissions = useAssetPermissionsState(assetId, isWhitelabel, isCodeSubdomain)
  const currentPermissions = assetPermissions?.currentPermissions
  const draftPermissions = assetPermissions?.draftPermissions

  const currentOrgPermission = useMemo(
    () => currentPermissions?.find(p => p.entityId === orgId),
    [currentPermissions, orgId]
  )

  const draftOrgPermission = useMemo(
    () => draftPermissions?.find(p => p.entityId === orgId),
    [draftPermissions, orgId]
  )

  const newOrgPermission = useMemo(
    () => ({
      entityId: orgId,
      entityName: orgName,
      entityType: EntityTypes.ORG,
      permissionName: PermissionName.EDITOR
    }),
    [orgName, orgId]
  )
  const organizationPermission = useMemo(() => {
    return draftOrgPermission ?? currentOrgPermission ?? newOrgPermission
  }, [draftOrgPermission, currentOrgPermission, newOrgPermission])

  const checkboxChecked = Boolean(draftOrgPermission)

  const addDraftPermission = assetPermissions?.addDraftPermission
  const removeDraftPermission = assetPermissions?.removeDraftPermission

  const onChangeCheckbox = useCallback(() => {
    const { Core_Domain_Settings_Clicked_Share_with_Everyone_Box } = eventNames
    if (!draftOrgPermission) {
      addDraftPermission?.(newOrgPermission)
    } else {
      removeDraftPermission?.(draftOrgPermission)
    }
    if (isWhitelabel) {
      fireHeapEvent(Core_Domain_Settings_Clicked_Share_with_Everyone_Box, {
        action: !draftOrgPermission ? 'select' : 'deselect'
      })
    }
    trackingClickShareWithEveryone?.(assetId, !draftOrgPermission)
  }, [
    draftOrgPermission,
    isWhitelabel,
    trackingClickShareWithEveryone,
    assetId,
    addDraftPermission,
    newOrgPermission,
    removeDraftPermission
  ])

  const pastTense = pastTenseLabel ? 'd' : ''
  const label = `Share${pastTense} with everyone in ${orgName || 'your organization'}`

  return {
    checkboxChecked,
    label,
    organizationPermission,
    onChangeCheckbox
  }
}
